<template>
    <div id="gis-chart" 
        class="gis-chart base-popup monitor-graph" 
        :style="css_props"
    >
        <v-card v-if="Object.keys(timeseries_data).length"
            class="base-popup__wrapper" 
        >
            <div id="base-popup__capture_range" 
                class="base-popup__container"
            >
                <div class="base-popup__tooltip">
                    <div class="base-popup__tooltip-title">
                        <div>
                            <div v-if="id === 'belongs_emap_chart'" 
                                class="back-station-title d-flex align-center"
                            >
                                <v-btn
                                    class="back-station-title__btn"
                                    color="white"
                                    icon
                                    @click="close_dialog"
                                >
                                    <v-icon color="white">
                                        icon-arrow-back
                                    </v-icon>
                                </v-btn>
                                <span class="back-station-title__text">
                                    {{ $t('__chart_common.back_to_gis_popup_table') }}
                                </span>
                            </div>
                            <div class="base-popup__tooltip-title__city_info">
                                <p v-show="county_title">
                                    {{ county_title }}
                                </p>
                                <h1>{{ station_title }}</h1>
                            </div>
                            <v-btn
                                class="base-popup__tooltip-close-btn"
                                color="white"
                                icon
                                @click="close_dialog"
                            >
                                <v-icon color="white">
                                    icon-close
                                </v-icon>
                            </v-btn>
                        </div>
                    </div>
                    <div class="base-popup__tooltip-selection">
                        <div class="selection-input">
                            <v-select
                                v-model="variable_selected"
                                :items="variable_select_options"
                                id="tsgraph-select-type"
                                item-text="text"
                                item-value="value"
                                :label="$t('__monitor_weather.weather_variable')"
                            />
                        </div>
                        <div v-if="variable_selected === 'rainfall'" 
                            class="selection-input"
                        >
                            <v-select
                                v-model="rain_selected"
                                :items="rain_select_options"
                                id="tsgraph-select-type-rainfall"
                                item-text="text"
                                item-value="value"
                                :label="$t('__timeseries.time_select_text')"
                            />
                        </div>
                        <div class="selection-input">
                            <v-select
                                v-model="day_selected"
                                :items="day_select_options"
                                id="tsgraph-select-day"
                                item-text="text"
                                item-value="value"
                                :label="$t('__chart_common.day_select_text')"
                            />
                        </div>
                        <div v-if="variable_selected === 'rainfall'" 
                            class="selection-input"
                        >
                            <v-select
                                v-model="yaxis_max_selected"
                                :items="yaxis_max_select_options"
                                id="tsgraph-select-val-max"
                                item-text="text"
                                item-value="value"
                                :label="$t('__chart_common.yaxis_max_select_text')"
                            />
                        </div>
                        <div v-if="has_cumulative_rainfall_data"
                            class="selection-input"
                        >
                            <v-select
                                v-model="yaxis2_max_selected"
                                :items="yaxis2_max_select_options"
                                id="tsgraph-select-val-max"
                                item-text="text"
                                item-value="value"
                                :label="$t('__chart_common.yaxis2_max_select_text')"
                            />
                        </div>
                    </div>
                </div>
                <div class="base-popup__content">
                    <div class="base-popup__content-detail d-flex justify-space-between">
                        <div class="d-flex align-center">
                            {{ is_mobile_width ? props_svg_sub_title.time : props_svg_sub_title.full_title }}
                            <v-btn v-if="!is_mobile_width"
                                id="tsgraph-reset-btn" 
                                icon
                            >
                                <v-icon color="black"
                                    @click="reset_time_range"
                                >
                                    icon-autorenew
                                </v-icon>
                            </v-btn>
                        </div>
                        <div v-if="!is_mobile_width">
                            <v-btn
                                id="tsgraph-download" 
                                @click="save_png"
                                icon
                            >
                                <v-icon color="black">
                                    icon-file-download
                                </v-icon>
                            </v-btn>
                            <v-btn
                                v-if="!is_pad_width"
                                class="close-btn"
                                color="black"
                                icon
                                @click="close_dialog"
                            >
                                <v-icon color="black">
                                    mdi-close
                                </v-icon>
                            </v-btn>
                        </div>
                        <v-menu v-if="is_mobile_width" 
                            offset-y
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="black"
                                    dark
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>icon-more</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item @click="reset_time_range">
                                    <v-icon color="black">
                                        icon-autorenew
                                    </v-icon>
                                    {{ $t('__chart_common.reset_time_range') }}
                                </v-list-item>
                                <v-list-item @click="save_png">
                                    <v-icon color="black">
                                        icon-file-download
                                    </v-icon>
                                    {{ $t('__chart_common.save_image') }}
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <div 
                        v-if="should_draw_histogram_diagram_header.includes(variable_selected)"
                        class="base-popup__content-legend"
                    >
                        <span class="legend histogram" /> {{ variable_selected_text }}
                        <template v-if="has_cumulative_rainfall_data">
                            <span class="legend line"/> {{ y_axis2.label }}
                        </template>
                        <span class="legend no-data-label">&#10010;</span> {{ $t('__chart_common.legend_no_data') }}
                    </div>
                    <div
                        v-else-if="should_draw_line_diagram_header.includes(variable_selected)"
                        class="base-popup__content-legend"
                    >
                        <span
                            class="legend" 
                            :class="{
                                line: variable_selected !== 'wind_direction',
                                circle: variable_selected === 'wind_direction'
                            }"
                        />
                        {{ variable_selected_text }}
                        <span class="legend no-data-label">&#10010;</span> 
                        {{ $t('__chart_common.legend_no_data') }}
                        <template v-if="need_extreme_value_headers.includes(variable_selected)">
                            <div>
                                <span class="legend triangle__wind-max" />
                                {{ `${$t('__monitor_weather.legend_max')}${variable_selected_text}` }}
                            </div>
                            <div>
                                <span class="legend triangle__wind-min" />
                                {{ `${$t('__monitor_weather.legend_min')}${variable_selected_text}` }}
                            </div>
                        </template>
                        <template v-if="variable_selected === 'wind_speed'">
                            <div>
                                <span class="legend triangle__gust-max" />
                                {{ `${$t('__monitor_weather.legend_max')}${$t('__monitor_weather.legend_gust')}` }}
                            </div>
                            <template v-if="day_selected === '1'">
                                <div>
                                    <span class="legend cross">&#10010;</span>
                                    <template v-if="language === 'en'">
                                        {{ `${legend_gust_interval} ${$t('__monitor_weather.legend_max')}${$t('__monitor_weather.legend_gust')}` }}
                                    </template>
                                    <template v-else>
                                        {{ `${legend_gust_interval}${$t('__monitor_weather.legend_max')}${$t('__monitor_weather.legend_gust')}` }}
                                    </template>
                                </div>
                                <div>
                                    <span class="legend gust_time">HH:MM</span>
                                    {{ `${$t('__monitor_weather.legend_gust')}${$t('__monitor_weather.legend_time_text')}` }}
                                </div>
                            </template>
                        </template>
                    </div>
                    <div class="base-popup__content-graph">
                        <QpeHistogram 
                            v-if="(
                                should_draw_histogram_diagram_header.includes(variable_selected) && 
                                is_show_chart
                            )"
                            ref="chart"
                            :id="id"
                            :choosed_time_series_data="choosed_time_series_data"
                            :choosed_time_series_line_data="choosed_time_series_line_data"
                            :x_axis_time_series_data="x_axis_time_series_data"
                            :svg_main_title="station_name"
                            :svg_sub_title="props_svg_sub_title.full_title"
                            :basic_layout_setting="basic_layout_setting"
                            :x_axis="x_axis"
                            :y_axis="y_axis"
                            :y_axis2="y_axis2"
                            :draw_custom_function="draw_custom_function"
                            :tooltip_value_text="tooltip_value_text"
                            :tooltip_value_unit="tooltip_value_unit"
                            :show_data_end_time="false"
                        />
                        <QpeLineChart
                            v-if="(
                                should_draw_line_diagram_header.includes(variable_selected) &&
                                is_show_chart
                            )"
                            ref="chart"
                            :id="id"
                            :choosed_time_series_data="choosed_time_series_data"
                            :x_axis_time_series_data="x_axis_time_series_data"
                            :svg_main_title="station_name"
                            :svg_sub_title="props_svg_sub_title.full_title"
                            :basic_layout_setting="basic_layout_setting"
                            :x_axis="x_axis"
                            :y_axis="y_axis"
                            :draw_custom_function="draw_custom_function"
                            :show_path="show_path_headers.includes(variable_selected)"
                            :show_extreme_value="need_extreme_value_headers.includes(variable_selected)"
                            :tooltip_value_text="tooltip_value_text"
                            :tooltip_value_unit="tooltip_value_unit"
                            :tooltip_value_icon_name="tooltip_value_icon_name"
                            :show_tick_color="!no_basic_tick_color_headers.includes(variable_selected)"
                            :show_data_end_time="false"
                            :gust_wind_data="gust_wind_data"
                            :gust_timeseries_data="gust_timeseries_data"
                        />
                    </div>

                    <div class="time-slider">
                        <div class="time-slider__content-container">
                            <span 
                                class="time-slider__content-before" 
                                @click="change_dragged_index_to_first"
                            />
                            <v-slider
                                v-model="dragged_index"
                                :max="max_dragged_index"
                                :min="min_dragged_index"
                                class="time-slider__content"
                                :tick-labels="slider_tick_labels"
                                ticks="always"
                                tick-size="4"
                            />
                            <span 
                                class="time-slider__content-after" 
                                @click="change_dragged_index_to_last"
                            />
                        </div>
                        <span class="time-slider__icon-container">
                            <v-icon 
                                class="time-slider__icon"
                                @click="minus_dragged_index_a_unit"
                            >
                                icon-backward-10
                            </v-icon>
                            <v-icon 
                                class="time-slider__icon"
                                @click="plus_dragged_index_a_unit"
                            >
                                icon-forward-10
                            </v-icon>
                        </span>
                    </div>
                </div>
            </div>
        </v-card>
        <div v-else-if="error_text">
            <v-alert
                :value="true"
                type="info"
                class="error__box"
                color="#ee3934"
            >
                {{ error_text }}
                <v-btn
                    class="error__close-btn"
                    color="white"
                    icon
                    @click="close_dialog"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-alert>
        </div>
        <LoadingHint v-else
            :text="$t('__chart_common.loading_text')"
        />
    </div>
</template>

<script>
    import { fetch_station_time_series_data } from "@/utils/chart/chart_ajax.js"
    import { 
        transform_datetime_str_to_chart_need_format,
    } from '@/utils/common/custom_time.js'
    import { 
        judge_value_valid,
        judge_extreme_value_valid,
    } from '@/utils/chart/verification.js'
    import { 
        draw_middle_text, 
        draw_wind_direction_middle_text_background,
        draw_wind_tier_middle_text_background 
    } from '@/utils/chart/d3_common.js'
    import { mapState, mapGetters } from 'vuex';
    import debounce from 'lodash/debounce';
    import * as d3 from 'd3';
    import QpeHistogram from '@/components/chart/qpe_histogram.vue'
    import QpeLineChart from '@/components/chart/qpe_line_chart.vue'
    import LoadingHint from '@/components/common/loading_hint.vue'

    export default {
        name: 'GisChart',
		
        components: { 
            QpeHistogram,
            QpeLineChart,
            LoadingHint,
        },

        props: {
            id: {
                type: String,
                default: 'individual_chart'
            },

            chart_options: {
                type: Object,
                default: () => ({}),
                require: true
            },

            default_variable: {
                type: String,
                default: '',
                require: true
            },

            prop_rain_selected: {
                type: String,
                default() {
                    return this.$t('__rainfall.hour_1')
                }
            },

            // 限制雨量累積時間下拉選單只顯示該表格實際有的時距，空陣列代表不限制(顯示全部選項)
            rain_period_options: {
                type: Array,
                default: () => []
            },
        },

        data() {
            return {
                timeseries_data: {},
                variable_selected: '',
                rain_selected: '',
                day_selected: '1',
                yaxis_max_selected: '0', //Y軸max值, 只有雨量有
                yaxis2_max_selected: '0', //第二Y軸max值, 只有雨量有
                
                basic_layout_setting: { //xy ticks都會畫在margin的範圍
                    'margin': {
                        'top': 50,
                        'right': 100,
                        'bottom': 50,
                        'left': 30,
                    }
                },
                
                dragged_index: 0,

                should_draw_histogram_diagram_header: [ 'rainfall' ],
                
                should_draw_line_diagram_header: [
                    'wind_direction', 'wind_speed', 'temperature', 'humidity',
                    'pressure',
                ],
                show_path_headers: [
                    'wind_speed', 'temperature', 'humidity', 'pressure',
                ],
                need_extreme_value_headers: [
                    'wind_speed', 'temperature', 'humidity', 'pressure',
                ],
                no_basic_tick_color_headers: [ 
                    'wind_direction', 'wind_speed'
                ],

                error_text: ''
            }
        },

        computed: {
            ...mapState([
                'client_name',
                'language',
            ]),

            ...mapState({
                is_atlas: state => state.atlas.is_atlas,
                is_monitor: state => state.monitor.is_monitor,
            }),

            ...mapGetters([
                'is_mobile_width',
                'is_pad_width',
            ]),

            is_show_chart() {
                const emap_condition = this.id === 'belongs_emap_chart' && !this.is_atlas && !this.is_monitor
                const monitor_condition = this.id === 'belongs_monitor_chart' && this.is_monitor
                return emap_condition || monitor_condition
            },

            css_props() {
                const get_total_slider_width = () => {
                    if (this.is_mobile_width) return 75
                    if (this.is_pad_width) return 90
                    return 55
                }

                const total_slider_width = get_total_slider_width()

                const interval_count =  6 + 1 - this.day_selected
                const slider_thumb_width = (total_slider_width) / interval_count
                const slider_width = total_slider_width - slider_thumb_width
                const slider_pseudo_width = slider_thumb_width / 2
                return {
                    '--slider-thumb-width': `${slider_thumb_width}vw`,
                    '--slider-width': `${slider_width}vw`,
                    '--slider-pseudo-width': `${slider_pseudo_width}vw`,
                }
            },
            
            options_for_trigger_and_fetch_data() {
                return {
                    ...this.chart_options,
                    lang: this.language,
                    client_name: this.client_name,
                }
            },

            choosed_time_series_data() {
                this.yaxis_max_selected //用來觸發重繪圖

                if (Object.keys(this.timeseries_data).length === 0) return []

                const time_series_key = this.variable_selected === 'rainfall' ? this.rain_selected : this.variable_value_mapping[this.variable_selected].data_key
                return this.timeseries_data[time_series_key].slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
            },

            choosed_time_series_line_data() {
                //目前只有rainfall有畫
                if (this.variable_selected !== 'rainfall') return []

                this.yaxis2_max_selected //用來觸發重繪圖

                if (Object.keys(this.timeseries_data).length === 0) return []

                //rainfall使用cumulative_rainfall_[1~6]_day資料畫折線
                const time_series_key = `cumulative_rainfall_${this.day_selected}_day`
                return this.timeseries_data[time_series_key].slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
            },

            // 判斷這個測站本身有沒有累積雨量資料(不受目前拖曳視窗影響)，用來決定第二Y軸相關UI要不要顯示
            has_cumulative_rainfall_data() {
                if (this.variable_selected !== 'rainfall') return false
                if (Object.keys(this.timeseries_data).length === 0) return false

                const time_series_key = `cumulative_rainfall_${this.day_selected}_day`
                return (this.timeseries_data[time_series_key] || []).length !== 0
            },

            gust_wind_data() {
                if (Object.keys(this.timeseries_data).length > 0 && this.variable_selected === 'wind_speed') {
                    console.log(this.timeseries_data)
                    return this.timeseries_data['瞬間風風速']
                        .slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
                        // 只保留每個瞬間風數值改變時的第一筆，不用 filter 的原因是要保留 index
                        .map((data, index, array) => index ===  0 || data !== array[index -1] ? data : '') 
                }
                
                return []
            },

            gust_timeseries_data() {
                if (Object.keys(this.timeseries_data).length > 0 && this.variable_selected === 'wind_speed') {   
                    return this.timeseries_data['瞬間風時間詳細']
                        .slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
                        .map((data, index, array) => index === 0 || data !== array[index -1] ? data : '')
                }

                return []
            },

            time_list() {
                return this.timeseries_data['資料時間'] ?? this.timeseries_data['data_time'] ?? []
            },

            max_dragged_index() {
                return this.time_list.length
            },

            x_axis_time_series_data() {
                return this.time_list.slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
            },

            county_title() {
                let county_title = ''
                county_title = this.timeseries_data['縣市'] ? `${county_title} ${this.timeseries_data['縣市']}` : county_title
                county_title = this.timeseries_data['鄉鎮'] ? `${county_title} ${this.timeseries_data['鄉鎮']}` : county_title
                if (county_title) {
                    return this.language === 'en' ? `${county_title} Station` : county_title
                }
                return ''
            },

            station_title() {
                const is_rainfall_selected = this.variable_selected === 'rainfall'
                const is_english = this.language === 'en'

                const station_name = is_rainfall_selected ? this.timeseries_data['station_name'] : this.timeseries_data['地面測站']
                const station_code = is_rainfall_selected ? this.timeseries_data['station_code'] : this.timeseries_data['地面測站代碼']

                return is_english ? `${station_name} Station  ${station_code}` : `${station_name}  ${station_code}`
            },

            station_name() {
                const city = this.timeseries_data['縣市']
                const town = this.timeseries_data['鄉鎮']
                const station_name = this.timeseries_data['地面測站']
                const station_end_text = this.language === 'en' ? ' Station' : '站'
                return `${city}/${town}/${station_name}${station_end_text}`
            },

            props_svg_sub_title() {
                const start_datetime = transform_datetime_str_to_chart_need_format(this.x_axis_time_series_data[0])
                const end_datetime = transform_datetime_str_to_chart_need_format(this.x_axis_time_series_data[this.x_axis_time_series_data.length-1])

                const get_rain_selected_title = () => {
                    const rain_selected_option = this.rain_select_options.find(({ value }) => value === this.rain_selected)
                    const rain_selected_text = rain_selected_option.text
                    return this.language === 'tw' ? 
                        `${rain_selected_text}${this.$t('__timeseries.accumlated_precp_timeseries_text')}` :
                        `${rain_selected_text} ${this.$t('__timeseries.accumlated_precp_timeseries_text')}`;
                }

                const get_variable_selected_title = () => {
                    const variable_selected_text_no_unit = this.variable_selected_text.split('(')[0]
                    return `${variable_selected_text_no_unit}${this.$t('__monitor_weather.timing_diagram_text')}`
                }

                const station = this.variable_selected === 'rainfall' ? get_rain_selected_title() : get_variable_selected_title();
                
                return {
                    full_title: `${station} ${start_datetime} ~ ${end_datetime}`,
                    station,
                    time: `${start_datetime} ~ ${end_datetime}`
                }
            },

            y_axis() {
                const {text, ticks, display_interval} = this.variable_value_mapping[this.variable_selected]
                return {
                    'label': text,
                    'ticks': ticks,
                    'display_interval': display_interval,
                    'label_distance': 50
                }
            },
            
            y_axis2() {
                const {text, ticks, display_interval} = this.variable_value_mapping[this.variable_selected]
                return {
                    'label': this.$t('__timeseries.y_axis2_label'),
                    'ticks': ticks,
                    'display_interval': display_interval,
                    'label_distance': 50
                }
            },

            variable_select_options() {
                const { type } = this.chart_options
                let basic_variable_select = []
                
                if ('gauge' in type) {
                    basic_variable_select = [
                        { text: this.$t('__rainfall.rainfall'), value: 'rainfall' }
                    ]
                }

                if ('weather' in type) {
                    basic_variable_select = [
                        ...basic_variable_select, 
                        { text: this.$t('__monitor_weather.wind_direction'), value: 'wind_direction' },
                        { text: this.$t('__monitor_weather.wind_speed'), value: 'wind_speed' },
                        { text: this.$t('__monitor_weather.temperature'), value: 'temperature' },
                        { text: this.$t('__monitor_weather.humidity'), value: 'humidity' },
                        { text: this.$t('__monitor_weather.pressure'), value: 'pressure' },
                    ]
                }
                
                return basic_variable_select
            },

            variable_selected_text() {
                return this.variable_value_mapping[this.variable_selected].text
            },

            rain_select_options() {
                const all_options = [
                    { text: this.$t('__rainfall.min_10'), value: 'min_10' },
                    { text: this.$t('__rainfall.hour_1'), value: 'hr_1' },
                    { text: this.$t('__rainfall.hour_3'), value: 'hr_3' },
                    { text: this.$t('__rainfall.hour_6'), value: 'hr_6' },
                    { text: this.$t('__rainfall.hour_12'), value: 'hr_12' },
                    { text: this.$t('__rainfall.hour_24'), value: 'hr_24' },
                    { text: this.$t('__rainfall.hour_48'), value: 'hr_48' },
                    { text: this.$t('__rainfall.hour_72'), value: 'hr_72' },
                ]

                if (!this.rain_period_options.length) {
                    return all_options
                }

                return all_options.filter(option => this.rain_period_options.includes(option.text))
            },

            x_axis() {
                return {
                    'label': `${this.$t('__monitor_weather.x_axis_label')}`,
                    'ticks': 24,
                    'display_interval': 2,
                    'label_distance': 40
                }
            },

            day_select_options() {
                return [
                    {'text': this.$t('__chart_common.day_1'), 'value':'1'}, 
                    {'text': this.$t('__chart_common.day_2'), 'value':'2'},
                    {'text': this.$t('__chart_common.day_3'), 'value':'3'}, 
                    {'text': this.$t('__chart_common.day_4'), 'value':'4'},
                    {'text': this.$t('__chart_common.day_5'), 'value':'5'}, 
                    {'text': this.$t('__chart_common.day_6'), 'value':'6'}
                ]
            }, 

            yaxis_max_select_options() {
                return [
                    {'text': '自動', 'value':'0'},
                    {'text': '50mm/hrs', 'value':'50'}, 
                    {'text': '300mm/hrs', 'value':'300'},
                ]
            }, 

            yaxis2_max_select_options() {
                return [
                    {'text': '自動', 'value':'0'},
                    {'text': '100mm/hrs', 'value':'100'},
                    {'text': '300mm/hrs', 'value':'300'},
                    {'text': '500mm/hrs', 'value':'500'},
                ]
            }, 

            variable_value_mapping() {
                return {
                    'rainfall': {
                        text: this.$t('__timeseries.y_axis_label'),
                        default_max: 300,
                        default_min: 0,
                        display_interval: 1,
                        ticks: 5,
                    },
                    'wind_direction': {
                        text: this.$t('__monitor_weather.wind_direction_text'),
                        data_key: '風向',
                        default_max: 360,
                        default_min: 0,
                        display_interval: 3,
                        ticks: 36,
                        icon: 'icon-wind',
                    }, 
                    'wind_speed': {
                        text: this.$t('__monitor_weather.wind_speed_text'),
                        data_key: '平均風',
                        display_interval: 1,
                        ticks: 10,
                    }, 
                    'temperature': {
                        text: this.$t('__monitor_weather.temperature_text'),
                        data_key: '溫度',
                        plus: 10,
                        minus: 5,
                        nan_max: 35,
                        nan_min: 15,
                        display_interval: 1,
                        ticks: 10,
                    }, 
                    'humidity': {
                        text: this.$t('__monitor_weather.humidity_text'),
                        data_key: '相對濕度',
                        default_max: 100,
                        default_min: 0,
                        display_interval: 1,
                        ticks: 10,
                    }, 
                    'pressure': {
                        text: this.$t('__monitor_weather.pressure_text'),
                        data_key: '氣壓',
                        plus: 10,
                        minus: 10,
                        nan_max: 1030,
                        nan_min: 1000,
                        display_interval: 1,
                        ticks: 10,
                    },
                }
            },

            tooltip_value_text() {
                const { text } = this.variable_value_mapping[this.variable_selected]
                return text.split('(')[0]
            },

            tooltip_value_unit() {
                const { text } = this.variable_value_mapping[this.variable_selected]
                return text.split('(')[1].replace(')', '')
            },

            tooltip_value_icon_name() {
                const { icon } = this.variable_value_mapping[this.variable_selected]
                return icon
            },

            min_dragged_index() {
                return this.max_dragged_index - 144*(6 - this.day_selected)
            },

            slider_tick_labels() {
                const valid_timeseries_data = this.time_list
                    .slice(this.min_dragged_index, this.max_dragged_index)
                    .map((time, index) => {
                        if (index % 144 === 72) {
                            const prev_day_num = (6 - this.day_selected) - ((index-72) / 144)
                            if (prev_day_num > 0) {
                                if (this.language === 'en') {
                                    if (prev_day_num === 1) {
                                        return '1 day ago'
                                    }
                                    return `${prev_day_num} days ago`
                                }
                                return `${prev_day_num}天前`
                            }
                        }
                        return ''
                    })
                return valid_timeseries_data
            },

            legend_gust_interval() {
                const gust_with_10_min = this.timeseries_data['瞬間風種類'] === '10分鐘最大瞬間風' ? this.$t('__monitor_weather.min_10') : ''
                const gust_with_1_hour = this.timeseries_data['瞬間風種類'] === '1小時最大瞬間風' ? this.$t('__monitor_weather.hour_1') : ''
                const gust_interval = gust_with_10_min || gust_with_1_hour || ''
                return gust_interval
            }
        },

        watch: {
            options_for_trigger_and_fetch_data: {
                immediate: true,
                async handler() {
                    await this.init_time_series_data()
                }
            },

            default_variable: {
                immediate: true,
                handler() {
                    const variable_selected_option = this.variable_select_options.find(({ text }) => text === this.default_variable)
                    this.variable_selected = variable_selected_option.value
                }
            },

            prop_rain_selected: {
                immediate: true,
                handler() {
                    const rain_selected_option = this.rain_select_options.find(({ text }) => text === this.prop_rain_selected)
                    this.rain_selected = rain_selected_option.value
                }
            },

            choosed_time_series_data: {
                immediate: true,
                handler() {
                    this.calc_y_axis_max_min()
                    this._call_rerender_function()
                }
            },

            choosed_time_series_line_data: {
                immediate: true,
                handler() {
                    this.calc_y_axis_max_min()
                    this._call_rerender_function()
                }
            }
        },

        methods: {            
            async init_time_series_data() {
                this.error_text = ''
                try {   
                    this.timeseries_data = await fetch_station_time_series_data(this.options_for_trigger_and_fetch_data)
                } catch (error_response) {
                    const failed_code = error_response.data?.failed_code ?? ''
                    if (failed_code.includes('station_code')) {
                        return this.error_text = this.$t('__chart_common.error_text.station_code')
                    }
                    return this.error_text = this.$t('__chart_common.error_text.default')
                }
                this.dragged_index = this.time_list.length;  
            },

            reset_time_range() {
                this.day_selected = '1'
                this.dragged_index = this.time_list.length
            },

            debounce_call_rerender_function: debounce(function() { // 不能 arrow function
                this._call_rerender_function()
            }, 150),

            change_dragged_index_to_first() {
                this.dragged_index = this.min_dragged_index
            },

            change_dragged_index_to_last() {
                this.dragged_index = this.max_dragged_index
            },

            minus_dragged_index_a_unit() {
                // v-model + v-slider 會幫忙把值鎖住
                this.dragged_index--
            },

            plus_dragged_index_a_unit() {
                this.dragged_index++
            },

            _call_rerender_function() {
                setTimeout(() => this.$refs.chart?.rerender_svg(), 0);
            },
            
            close_dialog() {
                this.$emit('close-chart-dialog');
            },
            
            save_png() {
                const {station_name, station_code, data_time} = this.timeseries_data
                const file_name = `${station_name}_${station_code}_${d3.timeFormat('%Y%m%d')(new Date())}_${data_time[data_time.length-1]}`
                this.$refs.chart.save_png(file_name)
            },
            
            calc_y_axis_max_min() {
                const { default_max, default_min, nan_max, nan_min , plus, minus } = this.variable_value_mapping[this.variable_selected]
                const ignore_None_data = this.choosed_time_series_data.filter(datum => judge_value_valid(datum))
                if(this.variable_selected === 'rainfall') {
                    this._calc_y_axis2_max_min_for_accumlated_precp(this.choosed_time_series_line_data)
                    return this._calc_y_axis_max_min_for_accumlated_precp(ignore_None_data)
                }

                if(this.variable_selected === 'wind_speed') {
                    const gust_ignore_None_data = this.gust_wind_data.filter(datum => judge_value_valid(datum))
                    return this._calc_y_axis_max_min_for_average_wind([...ignore_None_data, ...gust_ignore_None_data])
                }

                if (default_max !== undefined) {
                    this.y_axis.max_value = default_max
                } else {
                    const max_data_value = ignore_None_data.length > 0 ? Math.max(...ignore_None_data) : NaN
                    this.y_axis.max_value = judge_extreme_value_valid(max_data_value) ? max_data_value + plus : nan_max
                }

                if(default_min !== undefined) {
                    this.y_axis.min_value = default_min
                } else {
                    const min_data_value = ignore_None_data.length > 0 ? Math.min(...ignore_None_data) : NaN
                    this.y_axis.min_value = judge_extreme_value_valid(min_data_value) ? min_data_value - minus : nan_min 
                }
            },

            _calc_y_axis_max_min_for_accumlated_precp(ignore_None_data) {
                this.y_axis.min_value = 0

                const max_data_value = ignore_None_data.length > 0 ? Math.max(...ignore_None_data) : NaN
                this.y_axis.max_data_value = max_data_value //資料中的最大值

                const max_value = Number(this.yaxis_max_selected)
                if (max_value) {
                    this.y_axis.max_value = max_value
                    return
                }
                else if (isNaN(max_data_value)) {
                    const NAN_MAX_VALUE = 10
                    this.y_axis.max_value = NAN_MAX_VALUE
                    return
                }

                const MAX_RANGE = [10, 50, 100 , 300 , 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000]
                this.y_axis.max_value = MAX_RANGE.find(value => value >= max_data_value)
            },

            _calc_y_axis2_max_min_for_accumlated_precp(ignore_None_data) {
                this.y_axis2.min_value = 0

                const max_data_value = ignore_None_data.length > 0 ? Math.max(...ignore_None_data) : NaN
                this.y_axis2.max_data_value = max_data_value //資料中的最大值

                const max_value = Number(this.yaxis2_max_selected)
                if (max_value) {
                    this.y_axis2.max_value = max_value
                    return
                }
                else if (isNaN(max_data_value)) {
                    const NAN_MAX_VALUE = 10
                    this.y_axis2.max_value = NAN_MAX_VALUE
                    return
                }

                const MAX_RANGE = [10, 50, 100 , 300 , 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000]
                this.y_axis2.max_value = MAX_RANGE.find(value => value >= max_data_value)
            },

            _calc_y_axis_max_min_for_average_wind(ignore_None_data) {
                const max_data_value = Math.max(...ignore_None_data)
                this.y_axis.max_value = max_data_value >= 32.7 ? 56.1 : max_data_value >= 10.8 ? 37.0 : 13.9;
                this.y_axis.min_value = 0
            },

            draw_custom_function(svg, width, x, y, tooltip) {
                const margin = this.basic_layout_setting.margin
                const end_datetime_string = this.x_axis_time_series_data[this.x_axis_time_series_data.length-1]
                if(this.variable_selected === 'wind_direction') {
                    const WIND_DIR_ANGLE = [
                        { text: 'N', value: 0 }, { text: '', value: 45 },
                        { text: 'E', value: 90 }, { text: '', value: 135 },
                        { text: 'S', value: 180 }, { text: '', value: 225 },
                        { text: 'W', value: 270 }, { text: '', value: 315 },
                        { text: 'N', value: 360 },
                    ]
                    const TEXT_OFFSET = -2
                    draw_middle_text({
                        svg, x, y, end_datetime_string,
                        text_offset: TEXT_OFFSET,
                        data: WIND_DIR_ANGLE, 
                    })
                    draw_wind_direction_middle_text_background({ svg, margin, width })
                } else if(this.variable_selected === 'wind_speed') {
                    const TEXT_OFFSET = -2

                    const WIND_TIER_SPEED = [
                        0, 0.3, 1.6, 3.4, 5.5, 8.0, 10.8, 13.9, 17.2, 20.8, 24.5, 28.5, 32.7, 37.0, 41.5, 46.2, 51.0, 56.1
                    ]
                    const draw_tier = WIND_TIER_SPEED
                        .map((speed, index) => {
                            if (index === 0) {
                                return {
                                    text: '',
                                    value: speed
                                }
                            }

                            return {
                                text: this.language === 'en' ? `${this.$t('__monitor_weather.wind_level')} ${index}` : `${index} ${this.$t('__monitor_weather.wind_level')}`,
                                value: speed
                            }
                        })
                        .filter(data => data.value <= this.y_axis.max_value )
                    
                    draw_middle_text({
                        svg, x, y, end_datetime_string,
                        text_offset: TEXT_OFFSET,
                        data: draw_tier, 
                    }) 
                    draw_wind_tier_middle_text_background({svg, margin, width})

                    this.day_selected === '1' && this._draw_gust(svg, width, x, y, tooltip)
                    this._draw_gust_max(svg, width, x, y, tooltip)
                }
            },

            _draw_gust(svg, width, x, y, tooltip){
                const margin = this.basic_layout_setting.margin
                const time_parse = d3.timeParse('%Y-%m-%d %H:%M:%S')
                const is_gust_time_valid = (d, i) => x(time_parse(this.gust_timeseries_data[i])) > 0 && d
                // 十字
                svg.selectAll('path.gust_speed')
                    .data(this.gust_wind_data)
                    .enter()
                    .append('path')
                    .attr('class', 'gust_speed')
                    .attr('d', d3
                        .symbol()
                        .type(d3.symbolCross)
                    )
                    .attr('transform', (d, i) => {
                        if (is_gust_time_valid(d, i)) {
                            return `translate(${margin.left + x(time_parse(this.gust_timeseries_data[i]))}, ${y(d)}) scale(0.9)`;
                        }
                    })
                    .style('stroke-width', 0)
                    .style('fill', 'blue')
                    .style('opacity', (d,i) => is_gust_time_valid(d, i) ? 1: 0)
                    .on('mouseover', (d, i) => {
                        const gust_time_value = transform_datetime_str_to_chart_need_format(this.gust_timeseries_data[i])
                        tooltip
                            .transition()
                            .duration(200)
                            .style('opacity', 0.9);
                        tooltip
                            .html(`
                                ${gust_time_value}
                                <br>
                                ${this.$t('__monitor_weather.gust_value')}：${parseFloat(this.gust_wind_data[i])} m/s
                            `)
                            .style('left', `${d3.event.clientX}px`)
                            .style('top', `${d3.event.offsetY + 60}px`)
                            .style('transform', `translate(-50%, -100%)`)
                    })
                    .on('mouseout', () => {
                        tooltip.transition()
                            .duration(500)
                            .style('opacity', 0);
                    });

                svg.selectAll('text.gust_time')
                    .data(this.gust_wind_data)
                    .enter()
                    .append('text')
                    .attr('class', 'gust_time')
                    .attr('x', (d, i) => {
                        return margin.left + x(time_parse(this.gust_timeseries_data[i]));
                    })
                    .attr('y', (d) => {
                        return y(d) - 7.5;
                    })
                    .text((d, i) => {
                        if (is_gust_time_valid(d, i)) {
                            return this.gust_timeseries_data[i].slice(10, 16)
                        }
                    })
                    .style('text-anchor', 'middle')
                    .style('fill', '#B319EF')
                    .style('font-size', 12)
                    .style('font-weight', 'bold');
            },

            _draw_gust_max(svg, width, x, y, tooltip) {
                const margin = this.basic_layout_setting.margin
                let label_distance, label_value_distance, rotate_deg
                const time_parse = d3.timeParse('%Y-%m-%d %H:%M:%S')

                const float_gust_data = this.gust_wind_data
                    .map((value) => parseFloat(value))

                // 有可能會超出時間範圍，所以找第一個在時間範圍內的最大值
                const max_value = float_gust_data
                    .filter(d => !isNaN(d))
                    .sort((prev, next) => next - prev)
                    .find(datum => {
                        const datum_index = float_gust_data.lastIndexOf(datum)
                        return x(time_parse(this.gust_timeseries_data[datum_index])) >= 0
                    })

                const max_value_index = float_gust_data.lastIndexOf(max_value)
                const max_value_time = this.gust_timeseries_data[max_value_index]
                
                label_distance = -40
                label_value_distance = -55
                rotate_deg = 180

                const triangle = d3.symbol()
                    .type(d3.symbolTriangle)
                    .size(150)

                svg.append('path')
                    .attr('d', triangle)
                    .attr('fill', 'blue')
                    .attr('transform', `translate(${margin.left + x(time_parse(max_value_time))}, ${y(max_value)+ label_distance}) rotate(${rotate_deg})`)
                    .on('mouseover', () => {
                        const max_gust_time_value = transform_datetime_str_to_chart_need_format(max_value_time)
                        tooltip
                            .transition()
                            .duration(200)
                            .style('opacity', 0.9)
                        tooltip
                            .html(`
                                ${max_gust_time_value}
                                </br>
                                ${this.$t('__monitor_weather.gust_value')}：${parseFloat(max_value)} m/s
                            `)
                            .style('left', `${d3.event.clientX}px`)
                            .style('top', `${d3.event.offsetY + 60}px`)
                            .style('transform', `translate(-50%, -100%)`)
                    })
                    .on('mouseout', () => {
                        tooltip.transition()
                            .duration(500)
                            .style('opacity', 0);
                    })
                
                svg.append('text')
                    .attr('x', margin.left + x(time_parse(max_value_time)))
                    .attr('y', y(max_value) + label_value_distance)
                    .attr('text-anchor', 'middle')
                    .style('text-decoration', 'underline')  
                    .style('stroke', '#B319EF')
                    .style('opacity', 0.75)
                    .text(max_value)
            },
        },
    }
</script>

<style lang="scss" scoped>
    @import '../../../css/base_popup';

    .legend {
        margin: 2px 5px;
    }

    .histogram {
        display: inline-block;
        width: 1rem;
        height: 1rem;
        background-color: #4682b4;
    }

    .no-data-label {
        transform: rotate(45deg);
        font-weight: bold;
        color: #f00;
    }

    .line {
        display: inline-block;
        width: 2rem;
        border-top: 5px solid #4682b4;
    }

    @mixin triangle-base {
        width: 0;
        height: 0;
        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
    }

    .triangle {
        &__wind-max {
            @include triangle-base;

            border-top: 15px solid #f00;
            position: relative;
            top: 18px;
        }

        &__wind-min {
            @include triangle-base;

            position: relative;
            bottom: 18px;
            border-bottom: 15px solid #ffa500;
        }

        &__gust-max {
            @include triangle-base;

            border-top: 15px solid #00f;
            position: relative;
            top: 18px;
        }
    }

    .circle {
        display: inline-block;
        width: 1rem;
        height: 1rem;
        background-color: #4682b4;
        border-radius: 1rem;
    }

    .cross {
        font-weight: bold;
        color: #00f;
    }

    .gust_time {
        font-size: .8rem;
        color: #800080;
    }

    .back-station-title {
        margin-bottom: .25rem;

        @include laptop {
            position: absolute;
            top: 0;
            left: 16px;
        }

        &__text {
            font-size: .75rem;
            letter-spacing: .5px;
            line-height: 1.33;

            @include laptop {
                display: none;
            }
        }
    }

    .base-popup__tooltip-title__city_info {
        margin-left: .25rem;
    }

    .error {
        &__box {
            margin-bottom: 0;
        }

        &__close-btn {
            position: absolute;
            top: 0;
            right: 0;
        }
    }
</style>

<style lang="scss">
    .v-menu__content {
        z-index: 4002 !important;
    }

    @mixin time-slider-psuedo-comment {
        content: '';
        position: absolute;
        margin-top: 12px;
        width: var(--slider-pseudo-width);
        height: 8px;
        background: #e8ecf7;
        border-radius: 9ex;
    }

    .base-popup {
        .time-slider {
            width: 100%;
            text-align: center;

            &__content {
                position: relative;
                display: inline-block;
                width: var(--slider-width);

                &-container {
                    display: inline-block;
                    margin-left: calc(1rem + var(--slider-pseudo-width));
                    text-align: center;
                }

                &-before {
                    @include time-slider-psuedo-comment;

                    transform: translateX(calc(-100% + 1rem));
                }

                &-after {
                    @include time-slider-psuedo-comment;

                    transform: translateX(- 1rem);
                }
            }

            &__icon {
                transform: translateY(-50%);

                &::before {
                    margin: 0 .1rem;
                }

                &-container {
                    margin-left: calc(var(--slider-pseudo-width) - .5rem);
                }
            }
        }
    }

    .gis-chart .v-slider__thumb {
        transform: translate(-50%, -50%);
        width: var(--slider-thumb-width);
        height: 8px;
        border-radius: 12px;
        z-index: 1;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, .32);
        background-image: linear-gradient(to right, #124ed0 0%, #2366f8 99%);

        &::before {
            content: none;
        }

        &::after {
            content: 'lll';
            position: absolute;
            transform: translate(calc(var(--slider-thumb-width) / 2 - 50%), -40%);
            color: #fff;
        }
    }

    .gis-chart .v-slider__track-container {
        margin: 0 0 0 1px;
        height: 8px !important;
        background-color: #e8ecf7;

        .v-slider__track-background {
            background-color: #e8ecf7 !important;
        }

        .v-slider__track-fill {
            background-color: #e8ecf7 !important;
        }
    }

    .v-slider__ticks-container {
        .v-slider__tick {
            background-color: #e8ecf7 !important;

            &--filled {
                background-color: #e8ecf7 !important;
            }

            &-label {
                height: 14px;
                font-size: 11px;
                font-family: NotoSansCJKtc;
                letter-spacing: .5px;
                line-height: 1.27;
                color: #b3b7c4;
            }
        }
    }

    .back-station-title {
        .v-btn--icon.v-size--default {
            width: 1.5rem;
        }

        .v-btn::before {
            transform: translateX(-.65rem);
            width: 2rem;
        }

        .v-btn__content {
            margin-right: .75rem;
        }
    }
</style>
